import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, ArrowLeft } from 'lucide-react';

const FREE_FEATURES = [
  'Daily AI-generated affirmation',
  'Mood check-in & tracking',
  'Access to 2 audio categories',
  'Basic progress stats',
];

const PREMIUM_FEATURES = [
  'Unlimited AI affirmations',
  'Full audio library (all categories)',
  'Wealth Activation sessions',
  'Synced subtitle meditations',
  'Advanced mood & streak analytics',
  'Journal with guided prompts',
  'Priority new content access',
  'Offline listening (coming soon)',
];

export default function PricingPage() {
  const [billing, setBilling] = useState('yearly');

  const price = billing === 'yearly' ? 470 : 47;
  const perMonth = billing === 'yearly' ? Math.round(470 / 12) : 47;
  const savings = billing === 'yearly' ? (47 * 12) - 470 : 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 pt-8 pb-4">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-sm mb-12">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-4">
            Invest in your <span className="text-amber-400">inner world</span>
          </h1>
          <p className="text-zinc-400 text-base max-w-lg mx-auto">
            Start free. Upgrade when you're ready to unlock the full power of guided transformation.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-2 mb-12">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              billing === 'monthly'
                ? 'bg-zinc-800 text-white'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid="billing-monthly-btn"
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling('yearly')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              billing === 'yearly'
                ? 'bg-amber-500 text-zinc-900'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid="billing-yearly-btn"
          >
            Yearly
            {billing === 'yearly' && <span className="ml-1.5 text-xs font-bold">SAVE ${savings}</span>}
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="max-w-5xl mx-auto px-4 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">

          {/* Free Plan */}
          <div
            className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-8 flex flex-col"
            data-testid="free-plan-card"
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-zinc-400" />
              <h3 className="text-lg font-medium text-zinc-300">Free</h3>
            </div>
            <p className="text-zinc-600 text-sm mb-6">Begin your journey</p>

            <div className="mb-8">
              <span className="text-4xl font-serif text-white">$0</span>
              <span className="text-zinc-600 text-sm ml-1">/forever</span>
            </div>

            <ul className="space-y-3 mb-10 flex-1">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-zinc-600 mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <Link
              to="/register"
              className="block w-full text-center py-3 rounded-xl border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors text-sm font-medium"
              data-testid="free-plan-cta"
            >
              Get Started
            </Link>
          </div>

          {/* Premium Plan */}
          <div
            className="relative bg-gradient-to-b from-amber-900/20 via-zinc-900 to-zinc-950 border border-amber-500/30 rounded-2xl p-8 flex flex-col"
            data-testid="premium-plan-card"
          >
            {/* Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-amber-500 text-zinc-900 text-xs font-bold px-4 py-1 rounded-full">
                MOST POPULAR
              </span>
            </div>

            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-medium text-amber-300">Premium</h3>
            </div>
            <p className="text-zinc-500 text-sm mb-6">Unlock full transformation</p>

            <div className="mb-2">
              <span className="text-4xl font-serif text-white">${price}</span>
              <span className="text-zinc-500 text-sm ml-1">/{billing === 'yearly' ? 'year' : 'month'}</span>
            </div>
            {billing === 'yearly' && (
              <p className="text-amber-400/70 text-xs mb-6">~${perMonth}/month, billed annually</p>
            )}
            {billing === 'monthly' && <div className="mb-6" />}

            <ul className="space-y-3 mb-10 flex-1">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold text-sm transition-colors"
              data-testid="premium-plan-cta"
              onClick={() => {}}
            >
              Upgrade to Premium
            </button>
            <p className="text-center text-zinc-600 text-xs mt-3">Cancel anytime. No questions asked.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
